/**
 * Local Credential Source
 *
 * Stores credentials in a local encrypted vault file.
 * Uses age encryption for secure storage.
 */

import * as fs from 'fs-extra';
import * as path from 'path';
import type { ICredentialSource } from '../types';
import type { Credential, CredentialMetadata, VaultEntry, RotationEvent, AccessLogEntry } from '../../core/types';
import {
  AgeProvider,
  encryptIdentityWithPassphrase,
  decryptIdentityWithPassphrase,
  isPassphraseProtectedIdentity,
} from '../../encryption/age';
import { getVaultPath, getIdentityPath, getRecipientPath } from '../../config/paths';
import { promptPassphraseDecrypt } from '../../utils/prompts';
import { loadConfig } from '../../config/loader';
import { detectPromptLeak } from '../../core/validation';

/**
 * Module-level cache for decrypted identity
 * Persists across LocalSource instances within same process
 */
interface IdentityCache {
  key: string;
  expiresAt: number;
  identityPath: string;  // Track which identity file this was for
}

let identityCache: IdentityCache | null = null;

/**
 * Local credential source using age-encrypted vault
 */
export class LocalSource implements ICredentialSource {
  readonly id = 'local';
  readonly name = 'Local Vault';
  readonly priority = 1;

  private encryptionProvider: AgeProvider;
  private vaultPath: string;
  private identityPath: string;
  private recipientPath: string;

  constructor(options?: {
    vaultPath?: string;
    identityPath?: string;
    recipientPath?: string;
  }) {
    this.encryptionProvider = new AgeProvider();
    this.vaultPath = options?.vaultPath ?? getVaultPath();
    this.identityPath = options?.identityPath ?? getIdentityPath();
    this.recipientPath = options?.recipientPath ?? getRecipientPath();
  }

  /**
   * Check if the local vault is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      // Check if identity file exists
      const identityExists = await fs.pathExists(this.identityPath);
      return identityExists;
    } catch {
      return false;
    }
  }

  /**
   * Initialize the local vault
   * Creates identity file and vault directory
   */
  async initialize(options?: { passphrase?: string }): Promise<void> {
    // Generate new keypair
    const keyPair = await this.encryptionProvider.generateKeyPair();

    // Save identity (private key) - optionally encrypted with passphrase
    if (options?.passphrase) {
      const encryptedIdentity = await encryptIdentityWithPassphrase(
        keyPair.privateKey,
        options.passphrase
      );
      // Write as passphrase-protected format (marker + base64)
      const content = `# SecretSage passphrase-protected identity\n# Encrypted with scrypt\n${encryptedIdentity}\n`;
      await fs.ensureDir(path.dirname(this.identityPath));
      await fs.writeFile(this.identityPath, content, { mode: 0o600 });
    } else {
      await this.encryptionProvider.saveIdentity(keyPair.privateKey, this.identityPath);
    }

    // Save recipient (public key)
    await this.encryptionProvider.saveRecipient(keyPair.publicKey, this.recipientPath);

    // Create empty vault file
    await this.writeVault([]);
  }

  /**
   * Load identity, prompting for passphrase if protected
   * Uses caching to avoid repeated passphrase prompts
   */
  private async loadIdentity(): Promise<string> {
    // Check cache first
    if (identityCache && identityCache.identityPath === this.identityPath) {
      if (Date.now() < identityCache.expiresAt) {
        return identityCache.key;
      }
      // Cache expired, clear it
      identityCache = null;
    }

    const content = await fs.readFile(this.identityPath, 'utf8');

    if (isPassphraseProtectedIdentity(content)) {
      // Extract the base64 encrypted data (skip comment lines)
      const lines = content.split('\n').filter(l => !l.startsWith('#') && l.trim());
      const encryptedData = lines.join('');

      // Prompt for passphrase
      const passphrase = await promptPassphraseDecrypt();

      try {
        const decryptedKey = await decryptIdentityWithPassphrase(encryptedData, passphrase);

        // Cache the decrypted identity
        const config = await loadConfig();
        const ttlSeconds = config.encryption?.passphraseCacheTTL ?? 300;  // Default 5 minutes
        if (ttlSeconds > 0) {
          identityCache = {
            key: decryptedKey,
            expiresAt: Date.now() + (ttlSeconds * 1000),
            identityPath: this.identityPath,
          };
        }

        return decryptedKey;
      } catch {
        throw new Error('Invalid passphrase or corrupted identity file');
      }
    }

    // Not passphrase protected, load normally
    return this.encryptionProvider.loadIdentity(this.identityPath);
  }

  /**
   * Get a credential by name
   */
  async get(name: string, options?: { action?: 'read' | 'grant' }): Promise<Credential | null> {
    const entries = await this.readVault();
    const entryIndex = entries.findIndex((e) => e.name === name);

    if (entryIndex < 0) {
      return null;
    }

    const entry = entries[entryIndex];

    // Decrypt the value (will prompt for passphrase if needed)
    const privateKey = await this.loadIdentity();
    const value = await this.encryptionProvider.decrypt(entry.encryptedValue, privateKey);

    // Log the access
    await this.logAccess(entries, entryIndex, options?.action || 'read');

    return {
      name: entry.name,
      value,
      source: this.id,
      metadata: entry.metadata,
    };
  }

  /**
   * Log an access event to the credential's access log
   */
  private async logAccess(
    entries: VaultEntry[],
    index: number,
    action: 'read' | 'grant'
  ): Promise<void> {
    const entry = entries[index];
    const accessLog = entry.accessLog || [];

    // Add new entry
    accessLog.push({
      timestamp: new Date(),
      action,
    });

    // Keep only last 100 entries
    if (accessLog.length > 100) {
      accessLog.shift();
    }

    entries[index].accessLog = accessLog;
    await this.writeVault(entries);
  }

  /**
   * List all credential metadata
   */
  async list(): Promise<CredentialMetadata[]> {
    const entries = await this.readVault();
    return entries.map((entry) => ({
      name: entry.name,
      ...entry.metadata,
    }));
  }

  /**
   * Store a credential
   */
  async set(
    name: string,
    value: string,
    metadata?: Partial<CredentialMetadata>
  ): Promise<void> {
    // Defense-in-depth: refuse to encrypt a value that has the shape of the
    // pre-v0.5.0 "  KEY: value" display-layer formatter output. Guards
    // against any future code path that could re-introduce that symptom.
    const leak = detectPromptLeak(name, value);
    if (leak.leak) {
      throw new Error(
        `Refusing to store value: appears to be a "  ${name}: ..." formatted line, ` +
        `not a raw credential. If this is intentional, strip the prefix at the call site.`
      );
    }

    // Read current vault
    const entries = await this.readVault();

    // Get public key for encryption
    const publicKey = await this.encryptionProvider.loadRecipient(this.recipientPath);

    // Encrypt the value
    const encryptedValue = await this.encryptionProvider.encrypt(value, publicKey);

    // Find existing entry or create new
    const existingIndex = entries.findIndex((e) => e.name === name);
    const now = new Date();
    const isRotation = existingIndex >= 0;

    // Build rotation history
    let rotationHistory: RotationEvent[] = [];
    if (isRotation) {
      // Preserve existing history
      rotationHistory = entries[existingIndex].metadata?.rotationHistory || [];
      // Add new rotation event
      rotationHistory.push({
        timestamp: now,
        reason: metadata?.rotationHistory?.[0]?.reason,
      });
    }

    const newEntry: VaultEntry = {
      name,
      encryptedValue,
      metadata: {
        name,
        createdAt: isRotation ? entries[existingIndex].metadata?.createdAt : now,
        updatedAt: now,
        ...metadata,
        rotationHistory: isRotation ? rotationHistory : undefined,
      },
    };

    if (existingIndex >= 0) {
      entries[existingIndex] = newEntry;
    } else {
      entries.push(newEntry);
    }

    // Write updated vault
    await this.writeVault(entries);
  }

  /**
   * Delete a credential
   */
  async delete(name: string): Promise<boolean> {
    const entries = await this.readVault();
    const index = entries.findIndex((e) => e.name === name);

    if (index < 0) {
      return false;
    }

    entries.splice(index, 1);
    await this.writeVault(entries);
    return true;
  }

  /**
   * Search credentials by pattern
   */
  async search(pattern: string): Promise<CredentialMetadata[]> {
    const entries = await this.readVault();
    const regex = new RegExp(pattern, 'i');

    return entries
      .filter((e) => regex.test(e.name))
      .map((e) => ({
        name: e.name,
        ...e.metadata,
      }));
  }

  /**
   * Get all credentials (decrypted)
   */
  async getAll(): Promise<Credential[]> {
    const entries = await this.readVault();
    const privateKey = await this.loadIdentity();

    const credentials: Credential[] = [];

    for (const entry of entries) {
      const value = await this.encryptionProvider.decrypt(entry.encryptedValue, privateKey);
      credentials.push({
        name: entry.name,
        value,
        source: this.id,
        metadata: entry.metadata,
      });
    }

    return credentials;
  }

  /**
   * Get the public key (recipient)
   */
  async getPublicKey(): Promise<string> {
    return this.encryptionProvider.loadRecipient(this.recipientPath);
  }

  /**
   * Get the access log for a credential
   */
  async getAccessLog(name: string): Promise<AccessLogEntry[]> {
    const entries = await this.readVault();
    const entry = entries.find((e) => e.name === name);
    return entry?.accessLog || [];
  }

  /**
   * Read vault entries from file
   */
  private async readVault(): Promise<VaultEntry[]> {
    try {
      const exists = await fs.pathExists(this.vaultPath);
      if (!exists) {
        return [];
      }

      const content = await fs.readFile(this.vaultPath, 'utf8');
      if (!content.trim()) {
        return [];
      }

      return JSON.parse(content) as VaultEntry[];
    } catch (error) {
      // If file doesn't exist or is invalid, return empty
      return [];
    }
  }

  /**
   * Write vault entries to file
   */
  private async writeVault(entries: VaultEntry[]): Promise<void> {
    await fs.ensureDir(path.dirname(this.vaultPath));

    // Write with restrictive permissions
    const content = JSON.stringify(entries, null, 2);
    await fs.writeFile(this.vaultPath, content, { mode: 0o600 });
  }
}
